import { useState, useEffect } from 'react';
import { GraduationCap, Calendar, MapPin, Clock, Users, ExternalLink, RefreshCw } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ScrollArea } from '../components/ui/scroll-area';
import { PageHeader } from '../components/layout/PageHeader';
import { LoadingState } from '../components/common/LoadingState';
import { ErrorState, EmptyState } from '../components/common/ErrorState';
import { fetchWithCache } from '../services/cacheService';
import { API_BASE_URL } from '../config/api';

// URLs de los datasets de formación
const FORMACION_URLS = {
  2026: 'https://datos.tenerife.es/ckan/dataset/70d1454e-c560-4af0-9729-b892665d4953/resource/24dd5697-2f38-4a50-befb-65610afb374a/download/actividades-formativas-de-agrocabildo-durante-el-ano-2026.json',
  2025: 'https://datos.tenerife.es/ckan/dataset/70d1454e-c560-4af0-9729-b892665d4953/resource/e49d6a26-d847-4c84-9a40-9f4d7d4ec44a/download/actividades-formativas-de-agrocabildo-durante-el-ano-2025.json',
  2024: 'https://datos.tenerife.es/ckan/dataset/70d1454e-c560-4af0-9729-b892665d4953/resource/f646bdd5-9abf-4668-a1d8-5bae51c4c620/download/actividades-formativas-de-agrocabildo-durante-el-ano-2024.json',
};

const YEARS_AVAILABLE = [2026, 2025, 2024];
const TIPOS = ['Todos', 'Charla', 'Cursos', 'Cursillos', 'Demostración', 'Jornadas', 'Visita', 'Sesión teórico-práctica', 'Jornadas técnicas especiales'];

/**
 * Normaliza los datos de formación que vienen en formatos diferentes según el año
 */
function normalizeFormacionData(data, year) {
  const actividades = [];
  
  if (!data) return actividades;
  
  // Formato 2026: { agencias: [{ agencia_nombre, tipos_de_actividad: [{ actividad_tipo, actividades: [...] }] }] }
  if (data.agencias && Array.isArray(data.agencias)) {
    data.agencias.forEach(agencia => {
      const agenciaNombre = agencia.agencia_nombre;
      if (agencia.tipos_de_actividad) {
        agencia.tipos_de_actividad.forEach(tipoObj => {
          const tipo = tipoObj.actividad_tipo;
          if (tipoObj.actividades) {
            tipoObj.actividades.forEach(act => {
              actividades.push({
                id: act.actividad_id,
                titulo: act.actividad_titulo?.trim(),
                tipo: tipo,
                agencia: agenciaNombre,
                lugar: act.lugar_nombre,
                municipio: act.municipio_nombre,
                dias: act.actividad_dias,
                horas: act.actividad_horas,
                plazas: act.actividad_plazas,
                horario: act.actividad_horario,
                estado: act.actividad_estado,
                inicio: act.actividad_inicio ? act.actividad_inicio.split('T')[0] : null,
                fin: act.actividad_fin ? act.actividad_fin.split('T')[0] : null,
                inscripcion: act.inscripcion_estado,
              });
            });
          }
        });
      }
    });
  }
  // Formato 2025/2024: Array plano de actividades
  else if (Array.isArray(data)) {
    data.forEach(act => {
      actividades.push({
        id: act.actividad_id || act.id,
        titulo: (act.actividad_titulo || act.titulo)?.trim(),
        tipo: act.actividad_tipo || act.tipo,
        agencia: act.agencia_nombre || act.agencia,
        lugar: act.lugar_nombre || act.lugar,
        municipio: act.municipio_nombre || act.municipio,
        dias: act.actividad_dias || act.dias,
        horas: act.actividad_horas || act.horas,
        plazas: act.actividad_plazas || act.plazas,
        horario: act.actividad_horario || act.horario,
        estado: act.actividad_estado || act.estado,
        inicio: (act.actividad_inicio || act.inicio)?.split('T')[0],
        fin: (act.actividad_fin || act.fin)?.split('T')[0],
        inscripcion: act.inscripcion_estado || act.inscripcion,
      });
    });
  }
  
  return actividades;
}

export function FormacionPage() {
  const [actividades, setActividades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [tipoFilter, setTipoFilter] = useState('Todos');
  const [agenciaFilter, setAgenciaFilter] = useState('Todas');
  const [selectedYear, setSelectedYear] = useState(2026);

  // Obtener lista de agencias de los datos cargados
  const agencias = ['Todas', ...new Set(actividades.map(a => a.agencia).filter(Boolean).sort())];

  async function loadFormacion(year) {
    setLoading(true);
    setError(null);
    
    try {
      const url = FORMACION_URLS[year];
      if (!url) {
        throw new Error(`No hay datos disponibles para ${year}`);
      }
      
      // Usar proxy con caché
      const proxyUrl = `${API_BASE_URL}/api/ckan-proxy?url=${encodeURIComponent(url)}`;
      const result = await fetchWithCache(proxyUrl);
      
      const normalized = normalizeFormacionData(result.data, year);
      setActividades(normalized);
    } catch (err) {
      console.error('Error cargando formación:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadFormacion(selectedYear);
  }, [selectedYear]);

  const actividadesFiltradas = actividades
    .filter(act => {
      const matchSearch = !searchTerm || 
        act.titulo?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        act.lugar?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        act.municipio?.toLowerCase().includes(searchTerm.toLowerCase());
      const matchTipo = tipoFilter === 'Todos' || act.tipo === tipoFilter;
      const matchAgencia = agenciaFilter === 'Todas' || act.agencia === agenciaFilter;
      return matchSearch && matchTipo && matchAgencia;
    })
    .sort((a, b) => {
      // Ordenar por fecha de inicio ascendente (desde hoy hacia el futuro)
      const fechaA = a.inicio ? new Date(a.inicio) : new Date('9999-12-31');
      const fechaB = b.inicio ? new Date(b.inicio) : new Date('9999-12-31');
      return fechaA - fechaB;
    });

  function formatFecha(inicio, fin) {
    if (!inicio) return 'Por determinar';
    const fechaInicio = new Date(inicio);
    const opciones = { day: 'numeric', month: 'short', year: 'numeric' };
    
    if (inicio === fin || !fin) {
      return fechaInicio.toLocaleDateString('es-ES', opciones);
    }
    const fechaFin = new Date(fin);
    if (fechaInicio.getFullYear() === fechaFin.getFullYear()) {
      const opcionesCortas = { day: 'numeric', month: 'short' };
      return `${fechaInicio.toLocaleDateString('es-ES', opcionesCortas)} - ${fechaFin.toLocaleDateString('es-ES', opciones)}`;
    }
    return `${fechaInicio.toLocaleDateString('es-ES', opciones)} - ${fechaFin.toLocaleDateString('es-ES', opciones)}`;
  }

  function getEstadoBadge(estado, inscripcion) {
    if (inscripcion === 'Cerrada') {
      return <Badge variant="secondary" className="text-xs">Inscripción cerrada</Badge>;
    }
    if (estado === 'Planificado') {
      return <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700 border-blue-200">Planificado</Badge>;
    }
    if (estado === 'En curso / celebrado') {
      return <Badge variant="secondary" className="text-xs bg-green-50 text-green-700 border-green-200">Celebrado</Badge>;
    }
    return null;
  }

  if (loading) {
    return (
      <div className="px-4 py-4">
        <PageHeader title="Formación" subtitle={`Actividades formativas de AgroCabildo ${selectedYear}`} showBack />
        <LoadingState message={`Cargando actividades de ${selectedYear}...`} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="px-4 py-4">
        <PageHeader title="Formación" subtitle="Actividades formativas de AgroCabildo" showBack />
        <ErrorState message={error} onRetry={() => loadFormacion(selectedYear)} />
      </div>
    );
  }

  return (
    <div className="px-4 py-4" data-testid="formacion-page" role="main">
      <PageHeader 
        title="Formación" 
        subtitle={`${actividadesFiltradas.length} actividades de AgroCabildo ${selectedYear}`}
        showBack
        actions={
          <Button
            variant="ghost"
            size="sm"
            onClick={() => loadFormacion(selectedYear)}
            aria-label="Recargar datos"
            className="focus-visible:ring-2 focus-visible:ring-primary"
          >
            <RefreshCw size={16} aria-hidden="true" />
          </Button>
        }
      />

      {/* Selector de año y filtros */}
      <div className="mt-4 space-y-3">
        <div className="flex gap-2">
          <Select value={selectedYear.toString()} onValueChange={(v) => setSelectedYear(parseInt(v))}>
            <SelectTrigger className="w-[100px] bg-white" aria-label="Seleccionar año">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {YEARS_AVAILABLE.map(year => (
                <SelectItem key={year} value={year.toString()}>{year}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Input
            placeholder="Buscar actividad, lugar o municipio..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="bg-white flex-1"
            data-testid="search-actividad"
            aria-label="Buscar actividad"
          />
        </div>
        <div className="grid grid-cols-2 gap-2">
          <Select value={tipoFilter} onValueChange={setTipoFilter}>
            <SelectTrigger className="bg-white" aria-label="Filtrar por tipo">
              <SelectValue placeholder="Tipo" />
            </SelectTrigger>
            <SelectContent>
              {TIPOS.map(tipo => (
                <SelectItem key={tipo} value={tipo}>{tipo}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={agenciaFilter} onValueChange={setAgenciaFilter}>
            <SelectTrigger className="bg-white" aria-label="Filtrar por agencia">
              <SelectValue placeholder="Agencia" />
            </SelectTrigger>
            <SelectContent>
              {agencias.map(ag => (
                <SelectItem key={ag} value={ag}>{ag}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Enlace a inscripción */}
      <div className="mt-4">
        <Button 
          className="w-full focus-visible:ring-2 focus-visible:ring-primary"
          onClick={() => window.open('https://formacionagraria.tenerife.es/', '_blank')}
          aria-label="Abrir página de inscripción a actividades formativas"
        >
          <GraduationCap className="h-4 w-4 mr-2" aria-hidden="true" />
          Inscribirse en actividades
        </Button>
      </div>

      {/* Lista de actividades */}
      <ScrollArea className="mt-4 h-[500px]" aria-label="Lista de actividades formativas">
        {actividadesFiltradas.length === 0 ? (
          <EmptyState 
            title="Sin actividades"
            message="No hay actividades que coincidan con tu búsqueda"
            icon={GraduationCap}
          />
        ) : (
          <div className="space-y-3" role="list">
            {actividadesFiltradas.map(actividad => (
              <Card key={actividad.id} className="bg-white" role="listitem">
                <CardHeader className="pb-2">
                  <div className="flex items-start justify-between gap-2">
                    <CardTitle className="text-sm font-semibold leading-tight">{actividad.titulo}</CardTitle>
                  </div>
                  <div className="flex flex-wrap gap-2 mt-1">
                    <Badge variant="outline" className="text-xs">{actividad.tipo}</Badge>
                    {getEstadoBadge(actividad.estado, actividad.inscripcion)}
                  </div>
                </CardHeader>
                <CardContent className="space-y-2 text-sm">
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Calendar className="h-4 w-4 shrink-0" aria-hidden="true" />
                    <span>{formatFecha(actividad.inicio, actividad.fin)}</span>
                  </div>
                  
                  {actividad.lugar && (
                    <div className="flex items-start gap-2 text-muted-foreground">
                      <MapPin className="h-4 w-4 shrink-0 mt-0.5" aria-hidden="true" />
                      <div>
                        <span>{actividad.lugar}</span>
                        {actividad.municipio && <span className="block text-xs">{actividad.municipio}</span>}
                      </div>
                    </div>
                  )}

                  <div className="flex flex-wrap gap-3 text-xs text-muted-foreground">
                    {actividad.horas > 0 && (
                      <div className="flex items-center gap-1">
                        <Clock className="h-3 w-3" aria-hidden="true" />
                        <span>{actividad.horas}h</span>
                      </div>
                    )}
                    {actividad.plazas && (
                      <div className="flex items-center gap-1">
                        <Users className="h-3 w-3" aria-hidden="true" />
                        <span>{actividad.plazas} plazas</span>
                      </div>
                    )}
                    {actividad.horario && (
                      <span>{actividad.horario}</span>
                    )}
                  </div>

                  <div className="text-xs text-muted-foreground pt-1 border-t">
                    Agencia: {actividad.agencia}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </ScrollArea>

      {/* Enlace a fuente de datos */}
      <footer className="mt-4">
        <Button 
          variant="outline" 
          className="w-full focus-visible:ring-2 focus-visible:ring-primary"
          onClick={() => window.open('https://datos.tenerife.es/es/datos/conjuntos-de-datos/actividades-formativas-de-agrocabildo', '_blank')}
          aria-label="Ver datos en el portal de datos abiertos"
        >
          <ExternalLink className="h-4 w-4 mr-2" aria-hidden="true" />
          Ver en datos.tenerife.es
        </Button>
      </footer>
    </div>
  );
}

export default FormacionPage;
